import { Helmet } from "react-helmet-async";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { 
  ArrowRight, 
  Calendar,
  Edit,
  Building2,
  Users,
  Heart,
  FileText
} from "lucide-react";

const WikispacesHistory = () => {
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Wikispaces History - The Educators' Wiki Haven That Shaped Classroom Collaboration",
    "description": "The complete history of Wikispaces, the wiki hosting platform that revolutionized classroom collaboration for 13 years before shutting down in 2018.",
    "author": {
      "@type": "Organization",
      "name": "Chill Study Work"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Chill Study Work",
      "url": "https://chillstudy.work"
    },
    "datePublished": "2024-01-01",
    "dateModified": "2024-01-01",
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": "https://chillstudy.work/wikispaces/history/"
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Wikispaces History - The Educators' Wiki Haven | CSW</title>
        <meta name="description" content="The complete history of Wikispaces, the wiki hosting platform that revolutionized classroom collaboration for 13 years before shutting down in 2018." />
        <meta name="keywords" content="Wikispaces history, Wikispaces shutdown, classroom wiki history, TES Global acquisition, educational wiki platform" />
        <link rel="canonical" href="https://chillstudy.work/wikispaces/history/" />
        <meta property="og:title" content="Wikispaces History - The Educators' Wiki Haven" />
        <meta property="og:description" content="The complete history of Wikispaces, from 2005 creation to 2018 shutdown." />
        <meta property="og:url" content="https://chillstudy.work/wikispaces/history/" />
        <meta property="og:type" content="article" />
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      </Helmet>

      <Header />

      <main className="pt-24 pb-16">
        {/* Hero Section */}
        <section className="container mx-auto px-4 mb-16">
          <div className="max-w-4xl mx-auto">
            <Link to="/wikispaces/" className="inline-flex items-center gap-2 text-primary hover:underline mb-6">
              ← Back to Wikispaces Alternative
            </Link>
            <div className="inline-flex items-center gap-2 bg-emerald-500/10 text-emerald-500 px-4 py-2 rounded-full text-sm font-medium mb-6 ml-4">
              <Edit className="w-4 h-4" />
              2005 - 2018
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              The Educators' Wiki Haven
            </h1>
            <p className="text-xl text-muted-foreground leading-relaxed">
              How Wikispaces became the definitive platform for classroom collaboration, 
              hosting millions of educational wikis for 13 years.
            </p>
          </div>
        </section>

        {/* Timeline Section */}
        <section className="container mx-auto px-4 mb-16">
          <div className="max-w-4xl mx-auto">
            <div className="space-y-8">
              {/* 2005: Founding */}
              <div className="bg-card rounded-2xl p-8 border border-border/50 shadow-lg">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 rounded-lg bg-green-500/10 text-green-500">
                    <Calendar className="w-5 h-5" />
                  </div>
                  <span className="text-sm font-medium text-green-500">2005</span>
                </div>
                <h2 className="text-2xl font-bold mb-4">The Beginning</h2>
                <p className="text-muted-foreground mb-4">
                  Wikispaces was founded in 2005 by <strong>Adam Frey</strong> and <strong>James Byers</strong> under 
                  the company Tangient LLC. It started as one of the first major wiki hosting services, allowing 
                  anyone to create a free collaborative website.
                </p>
                <p className="text-muted-foreground">
                  From early on, Wikispaces had a strong focus on education. Its user-friendly interface made it 
                  especially appealing compared to the complexity of installing your own wiki software.
                </p>
              </div>

              {/* Education Focus */}
              <div className="bg-card rounded-2xl p-8 border border-border/50 shadow-lg">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 rounded-lg bg-blue-500/10 text-blue-500">
                    <Users className="w-5 h-5" />
                  </div>
                  <span className="text-sm font-medium text-blue-500">2008</span>
                </div>
                <h2 className="text-2xl font-bold mb-4">Investing in Education</h2>
                <p className="text-muted-foreground mb-4">
                  In 2008, Wikispaces launched a program giving away <strong>100,000 ad-free premium wikis to K-12 teachers</strong>. 
                  This seeding of the education market paid off enormously – within a few years, Wikispaces became 
                  <strong>the</strong> platform for classroom wikis worldwide.
                </p>
                <div className="bg-muted rounded-lg p-6 mb-4">
                  <h3 className="font-semibold mb-3">How Schools Used Wikispaces:</h3>
                  <ul className="space-y-2 text-muted-foreground">
                    <li className="flex items-start gap-2">
                      <Edit className="w-5 h-5 text-primary mt-0.5 shrink-0" />
                      <span><strong>Class websites:</strong> Teachers hosted course materials and announcements</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Edit className="w-5 h-5 text-primary mt-0.5 shrink-0" />
                      <span><strong>Student portfolios:</strong> Showcasing student work and growth over time</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Edit className="w-5 h-5 text-primary mt-0.5 shrink-0" />
                      <span><strong>Collaborative projects:</strong> Groups building knowledge bases together</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Edit className="w-5 h-5 text-primary mt-0.5 shrink-0" />
                      <span><strong>Club pages:</strong> Student organizations managing their own spaces</span>
                    </li>
                  </ul>
                </div>
                <p className="text-muted-foreground">
                  It wasn't uncommon to hear teachers casually reference "our class wiki" as a commonplace thing. 
                  Wikispaces lowered the barrier for schools to have an online presence.
                </p>
              </div>

              {/* Features & Appeal */}
              <div className="bg-card rounded-2xl p-8 border border-border/50 shadow-lg">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 rounded-lg bg-purple-500/10 text-purple-500">
                    <Heart className="w-5 h-5" />
                  </div>
                  <span className="text-sm font-medium text-purple-500">Peak Years</span>
                </div>
                <h2 className="text-2xl font-bold mb-4">Why Educators Loved It</h2>
                <p className="text-muted-foreground mb-4">
                  Wikispaces' hallmark was simplicity: even non-technical teachers and young students could make a page, 
                  insert text, images or videos, and have a functional website in minutes. It supported classroom 
                  management features like private wikis protected by a join code.
                </p>
                <p className="text-muted-foreground mb-4">
                  Over time, Wikispaces added discussion areas on each page, project areas for student teams, 
                  assessment tools for teachers to review contributions, and integrations with services like 
                  Google Apps. Students learned digital citizenship by collaborating on wiki pages.
                </p>
                <blockquote className="border-l-4 border-primary pl-4 italic text-muted-foreground">
                  "Unlike many tools that were read-only, Wikispaces was fully participatory, aligning well with 
                  constructivist learning models."
                </blockquote>
              </div>

              {/* TES Acquisition */}
              <div className="bg-card rounded-2xl p-8 border border-border/50 shadow-lg">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 rounded-lg bg-yellow-500/10 text-yellow-500">
                    <Building2 className="w-5 h-5" />
                  </div>
                  <span className="text-sm font-medium text-yellow-500">March 2014</span>
                </div>
                <h2 className="text-2xl font-bold mb-4">TES Global Acquisition</h2>
                <p className="text-muted-foreground mb-4">
                  Wikispaces was acquired by <strong>TES Global</strong>, a UK-based education company, in March 2014. 
                  This signaled an investment in continuing its service for educators.
                </p>
                <p className="text-muted-foreground">
                  However, the technology underpinning the site had aged significantly over nearly a decade of operation.
                </p>
              </div>

              {/* Shutdown */}
              <div className="bg-card rounded-2xl p-8 border border-border/50 shadow-lg">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 rounded-lg bg-red-500/10 text-red-500">
                    <Calendar className="w-5 h-5" />
                  </div>
                  <span className="text-sm font-medium text-red-500">2018</span>
                </div>
                <h2 className="text-2xl font-bold mb-4">The End of an Era</h2>
                <p className="text-muted-foreground mb-4">
                  In late January 2018, Wikispaces announced it would be shutting down. In a farewell message titled 
                  <em>"It's time for us to say farewell…"</em>, they cited <strong>cost and infrastructure issues</strong>.
                </p>
                <blockquote className="border-l-4 border-destructive pl-4 italic text-muted-foreground mb-4">
                  "The required investment to bring the infrastructure and code in line with modern standards is very substantial."
                </blockquote>
                <div className="bg-muted rounded-lg p-6 mb-4">
                  <h3 className="font-semibold mb-3">Shutdown Timeline:</h3>
                  <ul className="space-y-2 text-muted-foreground">
                    <li><strong>July 31, 2018:</strong> Classroom and basic (free) wikis closed</li>
                    <li><strong>January 31, 2019:</strong> All remaining wikis (including paid/private) shut down</li>
                  </ul>
                </div>
                <p className="text-muted-foreground">
                  The user base, while passionate, wasn't generating enough revenue to offset the costs of a 
                  massive infrastructure overhaul. TES Global decided it wasn't financially viable.
                </p>
              </div>

              {/* Legacy */}
              <div className="bg-card rounded-2xl p-8 border border-border/50 shadow-lg">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 rounded-lg bg-primary/10 text-primary">
                    <Heart className="w-5 h-5" />
                  </div>
                  <span className="text-sm font-medium text-primary">Legacy</span>
                </div>
                <h2 className="text-2xl font-bold mb-4">A Lasting Impact on Education</h2>
                <p className="text-muted-foreground mb-4">
                  The shutdown felt like the end of an era. Some teachers had been using Wikispaces for a decade or 
                  more – all their class materials, lesson plans, and student projects were embedded in it.
                </p>
                <blockquote className="border-l-4 border-destructive pl-4 italic text-muted-foreground mb-4">
                  "I have my teaching life on Wikispaces… over ten years of lesson plans and videos – I am distraught." 
                  — Teacher Linda's comment
                </blockquote>
                <p className="text-muted-foreground mb-4">
                  Wikispaces revolutionized how teachers approached online collaboration. It introduced countless 
                  classrooms to the concept of a wiki and collaborative knowledge-building. The idea that students 
                  could co-create content (not just consume) is now a given, partly thanks to Wikispaces paving the way.
                </p>
                <p className="text-muted-foreground">
                  The heartfelt comments and tributes around its closing time show that Wikispaces left an 
                  indelible mark on digital education.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* External References */}
        <section className="container mx-auto px-4 mb-16">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold mb-6">References & Further Reading</h2>
            <div className="space-y-3">
              <a 
                href="https://en.wikipedia.org/wiki/Wikispaces" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-3 p-4 bg-card rounded-lg border border-border/50 hover:border-primary/50 transition-colors"
              >
                <FileText className="w-5 h-5 text-primary" />
                <span>Wikipedia: Wikispaces</span>
                <ArrowRight className="w-4 h-4 ml-auto" />
              </a>
              <a 
                href="https://www.planeta.com/wikispaces/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-3 p-4 bg-card rounded-lg border border-border/50 hover:border-primary/50 transition-colors"
              >
                <FileText className="w-5 h-5 text-primary" />
                <span>Planeta: Wikispaces Says Farewell (with user comments)</span>
                <ArrowRight className="w-4 h-4 ml-auto" />
              </a>
              <a 
                href="https://askatechteacher.com/wikispaces-has-closed-what-are-your-alternatives/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-3 p-4 bg-card rounded-lg border border-border/50 hover:border-primary/50 transition-colors"
              >
                <FileText className="w-5 h-5 text-primary" />
                <span>Ask a Tech Teacher: Wikispaces Alternatives</span>
                <ArrowRight className="w-4 h-4 ml-auto" />
              </a>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-4">Continue the Collaborative Spirit</h2>
            <p className="text-xl text-muted-foreground mb-8">
              While Wikispaces is gone, collaborative learning continues. 
              Experience real-time collaboration with CSW's free study rooms.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" className="text-lg px-8">
                <a href="https://app.chillstudy.work" target="_blank" rel="noopener noreferrer">
                  Try CSW Free
                  <ArrowRight className="w-5 h-5 ml-2" />
                </a>
              </Button>
              <Button asChild size="lg" variant="outline" className="text-lg px-8">
                <Link to="/alternatives/">
                  View All Alternatives
                </Link>
              </Button>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default WikispacesHistory;
